// ═══════════════════════════════════════════════════════════════════════════
// moderation.js — content reports + (stubbed) auto image moderation.
//
// Layer 2 (live): users report content → content_reports table →
//   admin/manager Reports queue.
// Layer 3 (stub):  moderateImage(url) — auto-flag uploads via OpenAI
//   omni-moderation. Wired as a one-call hook; flip ON by implementing
//   /api/moderate-image (see the stub note below) + setting the key.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

// User-facing report reasons. id maps to the content_reports.reason
// CHECK constraint (migration 061). Keep in sync with that list.
export const REPORT_REASONS = [
  { id: 'nudity_sexual',   label: 'Nudity or sexual content' },
  { id: 'csam',            label: 'Child sexual abuse / minor safety' },
  { id: 'self_harm',       label: 'Self-harm or suicide' },
  { id: 'violence',        label: 'Violence or graphic content' },
  { id: 'hate_harassment', label: 'Hate speech or harassment' },
  { id: 'illegal',         label: 'Illegal or dangerous' },
  { id: 'spam_deception',  label: 'Spam or deception / impersonation' },
  { id: 'other',           label: 'Something else' },
];

const REASON_IDS = REPORT_REASONS.map(r => r.id);

// ── File a report ───────────────────────────────────────────────────────────
/**
 * @param {object} p
 *   contentType:   'clip' | 'profile' | 'episode' | 'comment' | 'show'
 *   contentId:     string (uuid, user_id, etc.)
 *   contentOwnerId:uuid of the content's owner (optional but useful)
 *   reason:        one of REASON_IDS
 *   details:       optional free text
 */
export async function reportContent({ contentType, contentId, contentOwnerId = null, reason, details = '' }) {
  if (!supabase) throw new Error('Not connected.');
  if (!REASON_IDS.includes(reason)) throw new Error('Pick a reason.');
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in to report.');

  const { error } = await supabase.from('content_reports').insert({
    reporter_id:      user.id,
    content_type:     contentType,
    content_id:       String(contentId),
    content_owner_id: contentOwnerId,
    reason,
    details: (details || '').slice(0, 2000),
    source: 'user',
  });
  if (error) throw error;
  return true;
}

// ── Admin / moderator queue ──────────────────────────────────────────────────
export async function listContentReports({ status = null } = {}) {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('list_content_reports', { p_status: status });
  if (error) {
    if (isMissingObjectError(error)) return [];
    console.error('listContentReports error:', error);
    return [];
  }
  return data || [];
}

export async function resolveContentReport(id, { status, action = null }) {
  if (!supabase || !id) throw new Error('Missing report id.');
  const { error } = await supabase.rpc('resolve_content_report', {
    p_id: id, p_status: status, p_action: action,
  });
  if (error) throw error;
  return true;
}

// ── Layer 3 hook: auto image moderation (STUB) ───────────────────────────────
/**
 * moderateImage(url) — returns { flagged: boolean, categories?: string[] }.
 *
 * STUB: currently always returns { flagged: false } so upload flows can
 * call it today without behavior change. To turn auto-flagging ON:
 *
 *   1. Add an OPENAI_API_KEY to the server env.
 *   2. Create api/moderate-image.js that POSTs to OpenAI's
 *      omni-moderation-latest model with the image URL:
 *
 *        const r = await fetch('https://api.openai.com/v1/moderations', {
 *          method: 'POST',
 *          headers: { authorization: `Bearer ${process.env.OPENAI_API_KEY}`,
 *                     'content-type': 'application/json' },
 *          body: JSON.stringify({
 *            model: 'omni-moderation-latest',
 *            input: [{ type: 'image_url', image_url: { url } }],
 *          }),
 *        });
 *        const j = await r.json();
 *        const res = j.results?.[0] || {};
 *        return { flagged: !!res.flagged,
 *                 categories: Object.entries(res.categories || {})
 *                   .filter(([,v]) => v).map(([k]) => k) };
 *
 *   3. Replace the body below with a fetch('/api/moderate-image', …) call.
 *
 * Upload helpers (uploadAvatar, uploadShowCover, clip upload) should
 * call this and, when flagged, refuse the upload + auto-insert a
 * content_reports row with source='auto' for human review.
 */
export async function moderateImage(/* url */) {
  return { flagged: false, categories: [] };
}

function isMissingObjectError(err) {
  if (!err) return false;
  const m = (err.message || '').toLowerCase();
  return m.includes('schema cache') || m.includes('does not exist')
      || m.includes('could not find') || err.code === 'PGRST202'
      || err.code === 'PGRST205' || err.code === '42P01';
}
